import { stringify } from 'hub-http/helpers/params';
import { generatePath, matchPath } from 'react-router-dom';
import BasePaths from 'automation-ui-routing/BasePaths';
import { parseLocation, getFlowEditRouteContextAware } from 'automation-ui-routing/utils';
import { ENROLLMENT, NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import ActionConfigBasePaths from 'automation-ui-action-features/actions/ActionConfigBasePaths';
import Routes from 'automation-ui-canvas/constants/Routes';
import { ACTION_SET, WORKFLOW } from 'automation-ui-canvas/flow/constants/FlowTypes';
import { CANVAS_ROOT, EMBEDDED_ACTION_EDIT, PLATFORM_ACTION_EDIT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getScreenForFlowType } from 'automation-ui-canvas/utils/getScreenForFlowType';

/*
 * !!! BEFORE ADDING TO THIS FILE !!!
 *
 * Consider instead adding to: utils.js, workflowsUrls.js, or
 * hubspotUrls.js in automation-ui-core
 *
 * HINT: If it has to do with react-router concepts such as location,
 * history, etc., keep it in here. Otherwise, consider the others.
 *
 */

// Action-specific routing

export const getNewActionConfigScrollToId = (...args) => `scroll-to-new-action-config-${args.join('-')}`;
export const getActionNameRouteFromFlow = (flow, actionId) => {
  // @ts-expect-error it's ok to get an invalid/undefined action
  const action = flow.getActions().get(actionId);
  const actionType = action ? action.getActionType() : null;
  return actionType ? ActionConfigBasePaths[actionType] : null;
};
export const getEnrollmentConfigRoute = ({
  flowId,
  isEmbedded,
  revisionId,
  isFlowInReview
}) => {
  if (isFlowInReview) {
    return `/${BasePaths.PLATFORM}/${BasePaths.FLOW}/${flowId}/${BasePaths.REVIEW}/${ENROLLMENT}`;
  } else {
    const flowRoute = getFlowEditRouteContextAware({
      flowId,
      isEmbedded,
      revisionId: parseInt(revisionId, 10)
    });
    return `${flowRoute}/${BasePaths.PLATFORM_FLOW_CONFIGS}/${ENROLLMENT}`;
  }
};

// History manipulation
export const buildSearchStringWithCurrentSourceFields = ({
  location,
  queryParams = {}
}) => {
  const search = new URLSearchParams(location.search);
  return stringify(Object.assign({
    sourceFlowId: search.get('sourceFlowId'),
    sourceEdgeId: search.get('sourceEdgeId')
  }, queryParams));
};
export const routeToActionList = (history, params, actionId, edgeId, newActionScreen, flowType = WORKFLOW) => {
  const {
    type: actionSubType,
    extensionId
  } = parseLocation(history.location);
  const screen = getScreenForFlowType(flowType);
  const pathname = generatePath(PLATFORM_ACTION_EDIT, {
    flowId: params.flowId,
    screen,
    actionId,
    configRoute: newActionScreen
  });
  const queryParams = {
    edgeId
  };
  const search = flowType === ACTION_SET ? buildSearchStringWithCurrentSourceFields({
    location: history.location,
    queryParams
  }) : stringify(queryParams);
  history.replace({
    pathname,
    search,
    state: {
      scrollToElementId: getNewActionConfigScrollToId(params.actionType, actionSubType || extensionId || 'default')
    }
  });
};
export const routeToEmbeddedNewActionList = (history, params, newActionScreen) => {
  history.replace(`/${BasePaths.EMBEDDED_CONFIGS}/${params.flowId}/${BasePaths.PLATFORM_FLOW_CONFIGS}/${NEW_ACTION}/${newActionScreen}${history.location.search}`);
};
export const routeToSpecificAction = ({
  history,
  configId,
  extraParams,
  flowId,
  edgeId,
  actionId,
  isEmbedded,
  flowType = WORKFLOW
}) => {
  let extraParamsStr = edgeId ? `edgeId=${edgeId}` : '';
  const configBaseRoute = ActionConfigBasePaths[configId];
  const screen = getScreenForFlowType(flowType);
  if (flowType === ACTION_SET) {
    extraParamsStr = buildSearchStringWithCurrentSourceFields({
      location: history.location,
      queryParams: {
        edgeId: edgeId
      }
    });
  }
  if (!configBaseRoute) {
    if (!isEmbedded) {
      // missing config route so reroute to flow
      history.replace({
        pathname: generatePath(CANVAS_ROOT, {
          flowId,
          screen
        }),
        search: history.location.search
      });
      return;
    } else {
      return;
    }
  }
  if (configBaseRoute) {
    extraParamsStr = `${extraParamsStr}&${stringify(extraParams)}`;
  }
  if (isEmbedded) {
    history.replace({
      pathname: generatePath(EMBEDDED_ACTION_EDIT, {
        flowId,
        actionId: actionId || NEW_ACTION,
        configRoute: configBaseRoute
      }),
      search: extraParamsStr
    });
  } else {
    history.replace({
      pathname: generatePath(PLATFORM_ACTION_EDIT, {
        flowId,
        screen,
        actionId: actionId || NEW_ACTION,
        configRoute: configBaseRoute
      }),
      search: extraParamsStr
    });
  }
};
export const setScrollToActionCardState = (actionId, history, scrollBehavior = 'smooth') => {
  const state = history.location.state;
  if (!actionId || state !== null && state !== void 0 && state.doNotScrollTo) {
    return;
  }
  history.replace({
    state: {
      scrollToAction: {
        actionId,
        scrollBehavior
      }
    },
    search: history.location.search
  });
};
export const clearScrollToActionCardState = history => {
  history.replace({
    state: {},
    search: history.location.search
  });
};
export const isActionConfigOpen = ({
  pathname
}) => {
  return !!matchPath(pathname, [Routes.PLATFORM_FLOW_CONFIGS, Routes.PLATFORM_FLOW_REVISION_CONFIGS]);
};
export const isEnrollmentConfigOpen = ({
  pathname
}) => {
  return !!matchPath(pathname, [Routes.FLOW_CANVAS_ENROLLMENT_CONFIG, Routes.FLOW_CANVAS_EVENT_ENROLLMENT, Routes.FLOW_CANVAS_SCHEDULED_ENROLLMENT]);
};